import type { ComponentProps } from 'react';
import type { Link } from '@/core/i18n/routing';
import type {
  AddToCartData,
  AddToCartEvent,
  BookingInteractionData,
  BookingInteractionEvent,
  BookingPurchaseData,
  BookingPurchaseEvent,
  CheckoutStepData,
  CheckoutStepEvent,
  ConfiguratorInteractionData,
  ConfiguratorInteractionEvent,
  ErrorData,
  ErrorEvent,
  ExperimentInteractionData,
  ExperimentInteractionEvent,
  FAQInteractionEvent,
  FormSubmissionData,
  FormSubmissionEvent,
  GA4TrackingData,
  InteractionEvent,
  InteractionType,
  LoginData,
  LoginEvent,
  MenuNavigationData,
  MenuNavigationEvent,
  OutOfStockSubmitEvent,
  PageViewEvent,
  PurchaseData,
  PurchaseEvent,
  RemoveFromCartData,
  RemoveFromCartEvent,
  SearchData,
  SearchEvent,
  SelectItemData,
  SelectItemEvent,
  SocialAuthData,
  SocialAuthEvent,
  ViewCartData,
  ViewCartEvent,
  ViewItemData,
  ViewItemEvent,
  ViewItemListEvent,
} from '@/ui/models/ga4';
import cleanPagePathname from '@/core/utils/clean-page-pathame';
import getPageType from '@/core/utils/get-page-type';
import hashEmail from '@/core/utils/hash-email';
import pushToDataLayer from '@/core/utils/push-to-data-layer';

export const trackPageView = async (data: GA4TrackingData): Promise<void> => {
  const emailHashed = data.user.email
    ? await hashEmail(data.user.email)
    : undefined;

  const event: PageViewEvent = {
    event: 'page_view',
    page_type: getPageType(data.pathname),
    country: data.country.toLowerCase(),
    locale: data.locale.toLowerCase(),
    user_id: data.user.id,
    email_hashed: emailHashed,
    logged_in: data.user.isLoggedIn,
    membership_type: data.user.membershipType,
    subscription_type: data.user.subscriptionType,
    page_path: cleanPagePathname(data.pathname),
  };

  pushToDataLayer(event);
};

export const trackMenuNavigation = (data: MenuNavigationData): void => {
  const event: MenuNavigationEvent = {
    event: 'menu_navigation',
    menu: data.menu,
    menu_path: data.menu_path,
    menu_text: data.menu_text,
    menu_click: data.menu_click,
  };

  pushToDataLayer(event);
};

export const trackFormSubmission = (data: FormSubmissionData): void => {
  const event: FormSubmissionEvent = {
    event: 'form_submission',
    form_name: data.form_name,
    form_extra_info: data.form_extra_info,
  };

  pushToDataLayer(event);
};

export const trackViewCart = (data: ViewCartData): void => {
  const event: ViewCartEvent = {
    event: 'view_cart',
    currency: data.currency,
    value: data.value,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackViewItem = (data: ViewItemData): void => {
  const event: ViewItemEvent = {
    event: 'view_item',
    currency: data.currency,
    out_of_stock: data.out_of_stock,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackSelectItem = (data: SelectItemData): void => {
  const event: SelectItemEvent = {
    event: 'select_item',
    currency: data.currency,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackAddToCart = (data: AddToCartData): void => {
  const event: AddToCartEvent = {
    event: 'add_to_cart',
    currency: data.currency,
    value: data.value,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackRemoveFromCart = (data: RemoveFromCartData): void => {
  const event: RemoveFromCartEvent = {
    event: 'remove_from_cart',
    currency: data.currency,
    value: data.value,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackConfiguratorInteraction = (
  data: ConfiguratorInteractionData,
): void => {
  const event: ConfiguratorInteractionEvent = {
    event: 'configurator_interaction',
    interaction_type: 'switch',
    configurator_step: data.configurator_step,
    configurator_step_number: data.configurator_step_number,
    configurator_detail_info: data.configurator_detail_info,
  };

  pushToDataLayer(event);
};

const STEP_NUMBERS: Record<CheckoutStepData['step_name'], number> = {
  personal_details: 1,
  delivery_options: 2,
  payment_details: 3,
  checkout_confirmation: 4,
};

export const trackCheckoutStep = (data: CheckoutStepData): void => {
  const event: CheckoutStepEvent = {
    event: 'checkout',
    step: STEP_NUMBERS[data.step_name],
    step_name: data.step_name,
    currency: data.currency,
    value: data.value,
    first_purchase: data.first_purchase,
    items: data.items,
  };

  pushToDataLayer(event);
};

const toLowercaseTrackingValue = <
  T extends string | ComponentProps<typeof Link>['href'] | null | undefined,
>(
  value: T,
): T => (typeof value === 'string' ? (value.toLowerCase() as T) : value);

export const trackInteraction = (data: {
  click_target: string | ComponentProps<typeof Link>['href'] | null | undefined;
  click_text?: string | null;
  interaction_type: InteractionType;
  content_name?: string | null;
}): void => {
  const event: InteractionEvent = {
    event: 'interaction',
    click_target: toLowercaseTrackingValue(data.click_target),
    click_text: toLowercaseTrackingValue(data.click_text),
    interaction_type: data.interaction_type,
    content_name: toLowercaseTrackingValue(data.content_name),
  };

  pushToDataLayer(event);
};

export const trackFAQInteraction = (data: {
  interaction_type: 'click';
  click_target: string;
}): void => {
  const event: FAQInteractionEvent = {
    event: 'faq_interaction',
    interaction_type: data.interaction_type,
    click_target: data.click_target,
  };

  pushToDataLayer(event);
};

export const trackPurchase = (data: PurchaseData): void => {
  const event: PurchaseEvent = {
    event: 'purchase',
    payment_type: data.payment_type,
    currency: data.currency,
    transaction_id: data.transaction_id,
    tax: data.tax,
    shipping: data.shipping,
    delivery_method: data.delivery_method,
    value: data.value,
    first_purchase: data.first_purchase,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackSearch = (data: SearchData): void => {
  const event: SearchEvent = {
    event: 'search',
    search_term: data.search_term,
    search_type: data.search_type,
    search_amount: data.search_amount,
  };

  pushToDataLayer(event);
};

export const trackLogin = async (data: LoginData): Promise<void> => {
  const emailHashed = await hashEmail(data.email);

  const event: LoginEvent = {
    event: 'login',
    country: data.country.toLowerCase(),
    locale: data.locale.toLowerCase(),
    user_id: data.user_id,
    email: emailHashed,
    logged_in: data.logged_in,
    method: data.method,
    ...(data.entry_point ? { entry_point: data.entry_point } : {}),
    ...(data.device_type ? { device_type: data.device_type } : {}),
  };

  pushToDataLayer(event);
};

export const trackSocialAuth = (data: SocialAuthData): void => {
  const event: SocialAuthEvent = {
    event: 'social_auth',
    social_provider: data.social_provider,
    social_action: data.social_action,
    entry_point: data.entry_point,
    device_type: data.device_type,
    ...(data.error_message ? { error_message: data.error_message } : {}),
  };

  pushToDataLayer(event);
};

export const trackError = (data: ErrorData): void => {
  const event: ErrorEvent = {
    event: 'error',
    error_type: data.error_type,
    error_message: data.error_message,
    error_code: data.error_code,
    error_page: data.error_page,
  };

  pushToDataLayer(event);
};

export const trackOutOfStockSubmit = (): void => {
  const event: OutOfStockSubmitEvent = {
    event: 'out_of_stock_submit',
  };

  pushToDataLayer(event);
};

export const trackViewItemList = (): void => {
  const event: ViewItemListEvent = {
    event: 'view_item_list',
  };

  pushToDataLayer(event);
};

export const trackBookingPurchase = (data: BookingPurchaseData): void => {
  const event: BookingPurchaseEvent = {
    event: 'purchase',
    payment_type: data.payment_type,
    currency: data.currency,
    transaction_type: data.transaction_type,
    value: data.value,
    coupon: data.coupon,
    transaction_id: data.transaction_id,
    tax: data.tax,
    shipping: data.shipping,
    items: data.items,
  };

  pushToDataLayer(event);
};

export const trackBookingInteraction = (data: BookingInteractionData): void => {
  const event: BookingInteractionEvent = {
    event: 'interaction',
    interaction_type: data.interaction_type,
    content_name: data.content_name,
    page_path: data.page_path,
    ...(data.click_text !== undefined && { click_text: data.click_text }),
  };

  pushToDataLayer(event);
};

export const trackExperimentInteraction = (
  data: ExperimentInteractionData,
): void => {
  const event: ExperimentInteractionEvent = {
    event: 'experiment_interaction',
    experiment_variant: data.experiment_variant,
    experiment_type: data.experiment_type,
    experiment_int_label: data.experiment_int_label,
    experiment_test_id: data.experiment_test_id,
    ...(data.product_code && { product_code: data.product_code }),
    ...(data.plan_type && { plan_type: data.plan_type }),
    ...(data.billing_period && { billing_period: data.billing_period }),
    ...(data.page_path && { page_path: data.page_path }),
  };

  pushToDataLayer(event);
};
